import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  LinearProgress,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  getLetterGrade,
  getXpProgress,
  ARCADE_ACCENT,
} from "../../utils/triviaArcade";
import TriviaShareCard from "./TriviaShareCard";
import TriviaResultsActions from "./TriviaResultsActions";
import TriviaLevelUpModal from "./TriviaLevelUpModal";
import { SPORT_BY_ID } from "../../constants/sportWorkspace";

const formatDuration = (milliseconds) => {
  const seconds = Math.max(0, Math.round((milliseconds || 0) / 1000));
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}:${String(remaining).padStart(2, "0")}`;
};

const TriviaResultsScreen = ({
  results,
  startLevel,
  startXp,
  reducedMotion,
  onPlayAgain,
  onBack,
  onViewLeaderboard,
  sport,
}) => {
  const [displayScore, setDisplayScore] = useState(0);
  const [showLevelUp, setShowLevelUp] = useState(false);

  const grade = getLetterGrade(results?.accuracy || 0);
  const xpAfter = (startXp || 0) + (results?.experiencePoints || 0);
  const xpProgress = getXpProgress(xpAfter);
  const leveledUp = (results?.newLevel || startLevel) > startLevel;

  useEffect(() => {
    if (reducedMotion) {
      setDisplayScore(results?.finalScore || 0);
      return undefined;
    }
    const target = results?.finalScore || 0;
    const start = performance.now();
    const duration = 600;
    let frame;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setDisplayScore(Math.round(target * t));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reducedMotion, results?.finalScore]);

  useEffect(() => {
    if (leveledUp) setShowLevelUp(true);
  }, [leveledUp]);

  const isDaily = results?.gameMode === "daily";

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal,
        bgcolor: "background.default",
        overflow: "auto",
        p: { xs: 2, sm: 4 },
      }}
    >
      <Box sx={{ maxWidth: 640, mx: "auto" }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" sx={{ mb: 3 }}>
          Game Complete
        </Typography>

        <Box sx={{ textAlign: "center", mb: 3 }}>
          <motion.div
            initial={reducedMotion ? false : { scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={reducedMotion ? { duration: 0 } : { type: "spring" }}
          >
            <Typography
              variant="h1"
              fontWeight={900}
              sx={{ color: ARCADE_ACCENT, lineHeight: 1 }}
            >
              {grade}
            </Typography>
            <Typography variant="h3" fontWeight={700} sx={{ mt: 1 }}>
              {displayScore}
            </Typography>
          </motion.div>
        </Box>

        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
          <Chip label={`${Math.round((results?.accuracy || 0) * 100)}% accuracy`} />
          <Chip label={formatDuration(results?.totalTime)} />
          <Chip label={`Best streak ${results?.bestStreakThisGame || 0}`} />
        </Stack>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            +{results?.experiencePoints || 0} XP · Level {xpProgress.level}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={xpProgress.percent}
            sx={{ height: 10, borderRadius: 999 }}
          />
        </Box>

        {results?.achievementsUnlocked?.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 3, overflowX: "auto" }}>
            {results.achievementsUnlocked.map((achievement) => (
              <Chip
                key={achievement.id || achievement.name}
                label={achievement.name}
                color="success"
              />
            ))}
          </Stack>
        )}

        <TriviaShareCard
          mode={results?.gameMode || "quick"}
          grade={grade}
          correct={results?.correctAnswers || 0}
          total={results?.totalQuestions || 0}
          score={results?.finalScore || 0}
          shareToken={results?.shareToken}
          username={results?.username}
          sportLabel={SPORT_BY_ID[results?.sport || sport]?.label}
        />

        <TriviaResultsActions
          isDaily={isDaily}
          onPlayAgain={onPlayAgain}
          onBack={onBack}
          onViewLeaderboard={onViewLeaderboard}
        />
      </Box>

      <TriviaLevelUpModal
        open={showLevelUp}
        newLevel={results?.newLevel}
        onClose={() => setShowLevelUp(false)}
        reducedMotion={reducedMotion}
      />
    </Box>
  );
};

export default TriviaResultsScreen;
